import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, FolderOpen, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_CASE_TYPES, PRACTICE_JURISDICTIONS } from "@/constants/practice";

const TrialOrganizerPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    case_name: '',
    trial_date: '',
    witnesses: [],
    exhibits: [],
    themes: []
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice/trial-organizer`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="trial-organizer-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Trial Organizer</h1>
          <p className="text-muted-foreground">Generate comprehensive trial binder and organization system</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Case Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Case Name</Label>
                    <Input placeholder="Smith v. Jones" value={form.case_name} onChange={e => setForm(f => ({...f, case_name: e.target.value}))} />
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>Case Type</Label>
                      <Select value={form.case_type} onValueChange={v => setForm(f => ({...f, case_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Jurisdiction</Label>
                      <Select value={form.jurisdiction} onValueChange={v => setForm(f => ({...f, jurisdiction: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Trial Date</Label>
                      <Input type="date" value={form.trial_date} onChange={e => setForm(f => ({...f, trial_date: e.target.value}))} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-indigo-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-indigo-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.case_name}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><FolderOpen className="h-5 w-5" /> Generate Organizer</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-indigo-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">Trial Organization System</h2>
                <p className="text-white/80">{result.case_name} • {result.trial_date || 'Date TBD'}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Organizer</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle>Organization System</CardTitle></CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.organization_system}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader><CardTitle>Quick Objection Reference</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2">
                  {Object.entries(result.objections_reference || {}).map(([key, value]) => (
                    <div key={key} className="p-3 bg-slate-50 rounded">
                      <p className="font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

// Settlement Negotiation Page

export default TrialOrganizerPage;
